import { Box, SxProps, Theme, TypographyProps } from '@mui/material';
import MuiAccordionSummary from '@mui/material/AccordionSummary';
import { FC, ReactNode } from 'react';

import { AbgTypography, Caret, formatMoney, mergeSx } from '@abg/kit-base';
import { AbgSxProps } from '@abg/typings';

import { CustomMuiAccordionSummaryClass } from '../../car-info-accordion.constants';
import { accordionSummaryClass } from '../../car-info-accordion.helpers';
import { CarInfoAccordionProps } from '../../car-info-accordion.types';
import {
  carInfoAccordionStyling,
  checkoutMobileAccordionSummaryStyling,
} from '../../CarInfoAccordion.styling';

import {
  carInfoSummaryStyling,
  checkoutMobileCommonStyles,
  checkoutMobileWrapper,
} from './CarInfoSummary.styling';

export const CarInfoSummary: FC<CarInfoSummaryProps> = ({
  testId,
  headerText,
  infoMessage,
  recentCost,
  previousCost,
  carInfoStyling,
  activeSummary,
  hasExpandIcon = true,
  children,
  headerVariant,
  currency = 'USD',
  isCheckoutMobile,
  hasPricingData,
}) => {
  const setAccordionSummaryClassName = accordionSummaryClass(
    CustomMuiAccordionSummaryClass,
  );

  const shouldRenderOldPrice =
    !!previousCost && !!recentCost && previousCost > recentCost;

  return (
    <MuiAccordionSummary
      className={setAccordionSummaryClassName(activeSummary)}
      expandIcon={hasExpandIcon && <Caret sx={carInfoAccordionStyling.caret} />}
      sx={
        {
          ...carInfoAccordionStyling.accordionSummary,
          ...(isCheckoutMobile ? checkoutMobileAccordionSummaryStyling : {}),
        } as AbgSxProps
      }
    >
      {children || (
          <Box
            sx={
              {
                ...mergeSx(
                  carInfoSummaryStyling.wrapper,
                  carInfoStyling?.wrapperStyles,
                ),
                ...(isCheckoutMobile ? checkoutMobileWrapper : {}),
              } as AbgSxProps
            }
          >
            <Box sx={carInfoSummaryStyling.textContainer}>
              <AbgTypography
                variant={headerVariant}
                sx={
                  {
                    ...mergeSx(
                      carInfoSummaryStyling.headerText,
                      carInfoStyling?.headerTextStyles,
                    ),
                    ...(isCheckoutMobile ? checkoutMobileCommonStyles : {}),
                  } as AbgSxProps
                }
                data-testid={`${testId}-label`}
              >
                {headerText}
              </AbgTypography>

              {infoMessage && (
                <AbgTypography
                  data-testid={`${testId}-info`}
                  sx={mergeSx(carInfoSummaryStyling.infoMessage)}
                >
                  {infoMessage}
                </AbgTypography>
              )}
            </Box>

            <Box
              sx={mergeSx(
                carInfoSummaryStyling.costsContainer,
                carInfoStyling?.costsContainerStyles,
              )}
            >
              <AbgTypography
                component="span"
                variant={headerVariant}
                data-testid={`${testId}-previous-cost`}
                sx={
                  {
                    ...mergeSx(
                      carInfoSummaryStyling.previousCost,
                      carInfoStyling?.previousCostStyles,
                    ),
                    ...(isCheckoutMobile ? checkoutMobileCommonStyles : {}),
                  } as AbgSxProps
                }
              >
                {shouldRenderOldPrice &&
                  hasPricingData &&
                  formatMoney(previousCost || 0, { currency: currency ?? '' })}
              </AbgTypography>

              <AbgTypography
                variant={
                  isCheckoutMobile ? 'bodySmallMedium' : 'bodySmallRegular'
                }
                data-testid={`${testId}-recent-cost`}
                sx={
                  {
                    ...mergeSx(
                      carInfoSummaryStyling.recentCost,
                      carInfoStyling?.recentCostStyles,
                    ),
                    ...(isCheckoutMobile ? checkoutMobileCommonStyles : {}),
                  } as AbgSxProps
                }
              >
                {hasPricingData &&
                  formatMoney(recentCost || 0, { currency: currency ?? '' })}
              </AbgTypography>
            </Box>
          </Box>
        ) ||
        'No data provided'}
    </MuiAccordionSummary>
  );
};

type CarInfoSummaryProps = {
  testId?: string;
  headerText?: string;
  infoMessage?: string;
  recentCost?: number | undefined;
  activeSummary?: string;
  hasExpandIcon?: boolean;
  children?: ReactNode;
  duration?: string | undefined;
  previousCost?: number | undefined;
  currency?: string | undefined;
  headerVariant?: TypographyProps['variant'];
  durationVariant?: TypographyProps['variant'];
  styleParts?: CarInfoAccordionProps['styleParts'];
  carInfoStyling?: {
    [key in ClassesFields]?: SxProps<Theme> | undefined;
  };
  isCheckoutMobile?: boolean | undefined;
  hasPricingData?: boolean | undefined;
};

type ClassesFields =
  | 'wrapperStyles'
  | 'costsContainerStyles'
  | 'recentCostStyles'
  | 'previousCostStyles'
  | 'headerTextStyles';
